import CloseIcon from "@mui/icons-material/Close";
import { Alert, Box, Button, Chip, Grid, Icon, IconButton, Modal, Stack, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useEffect, useMemo } from "react";
import * as yup from "yup";
import { LoadingComp } from "../../../../shared/components/loading/Loading";
import { IVFormErros, useVForm, VForm } from "../../../../shared/forms";
import { VMaskTextField } from "../../../../shared/forms/VMaskTextField";
import { globalRemoveMaskReal2 } from "../../../../shared/utils";

interface ModalSetupFeeProps {
	isOpen: boolean;
	onClose: () => void;
	minValue: number;
	maxValue: number;
	defaultValue?: string;
	isLoading?: boolean;
	onConfirm: (amount: number) => void | Promise<void>;
}

type FormData = {
	amount: string;
};

export const ModalSetupFee: React.FC<ModalSetupFeeProps> = ({
	isOpen,
	onClose,
	minValue,
	maxValue,
	defaultValue,
	isLoading = false,
	onConfirm,
}) => {
	const { formRef } = useVForm();

	const clampToLimits = (value: number) => {
		const safeMin = Number.isFinite(minValue) ? minValue : 0;
		const safeMax = Number.isFinite(maxValue) ? maxValue : safeMin;
		if (!Number.isFinite(value)) return safeMin;
		return Math.min(Math.max(value, safeMin), safeMax);
	};

	const formatCurrencyInput = (value: number) => {
		const safeValue = Number.isFinite(value) ? value : 0;
		return safeValue.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
	};

	const modalStyle = useMemo(
		() => ({
			position: "relative" as const,
			top: "50%",
			left: "50%",
			transform: "translate(-50%, -50%)",
			width: "min(680px, 92vw)",
			maxWidth: "70%",
			bgcolor: "background.paper",
			border: "1px solid #605e5e",
			boxShadow: 24,
			p: 2,
			borderRadius: "6px",
			display: "flex",
			flexDirection: "column" as const,
			backgroundColor: "#FFF",
		}),
		[],
	);

	const minLabel = useMemo(
		() => (Number.isFinite(minValue) ? minValue.toLocaleString("pt-br", { style: "currency", currency: "BRL" }) : "N/D"),
		[minValue],
	);
	const maxLabel = useMemo(
		() => (Number.isFinite(maxValue) ? maxValue.toLocaleString("pt-br", { style: "currency", currency: "BRL" }) : "N/D"),
		[maxValue],
	);

	useEffect(() => {
		if (!isOpen) return;
		const rawInitialAmount = String(defaultValue ?? minValue ?? "0");
		const numericInitialAmount = Number(globalRemoveMaskReal2(rawInitialAmount));
		const clampedInitialAmount = clampToLimits(numericInitialAmount);
		const initialAmount = formatCurrencyInput(clampedInitialAmount);
		formRef.current?.setErrors({});
		formRef.current?.setData({ amount: initialAmount });

		const timeoutId = window.setTimeout(() => {
			formRef.current?.setData({ amount: initialAmount });
		}, 0);

		return () => window.clearTimeout(timeoutId);
	}, [isOpen, defaultValue, minValue]);

	const validationSchema: yup.SchemaOf<FormData> = yup.object().shape({
		amount: yup
			.string()
			.required("Campo obrigatório")
			.test("valid-number", "Informe um valor válido", (value) => {
				const numericValue = Number(globalRemoveMaskReal2(String(value ?? "")));
				return Number.isFinite(numericValue);
			}),
	});

	const handleSubmit = async (data: FormData) => {
		try {
			formRef.current?.setErrors({});

			const rawValue = String(data.amount ?? "");
			const numericValue = Number(globalRemoveMaskReal2(rawValue));
			if (!Number.isFinite(numericValue)) {
				formRef.current?.setErrors({ amount: "Informe um valor válido" });
				return;
			}

			const clamped = clampToLimits(numericValue);
			if (clamped !== numericValue) {
				formRef.current?.setFieldValue("amount", formatCurrencyInput(clamped));
			}

			await validationSchema.validate({ amount: formatCurrencyInput(clamped) }, { abortEarly: false });
			await onConfirm(clamped);
		} catch (err: any) {
			if (err?.name === "ValidationError") {
				const validationErros: IVFormErros = {};
				err.inner.forEach((error: any) => {
					if (!error.path) return;
					validationErros[error.path] = error.message;
				});
				formRef.current?.setErrors(validationErros);
			}
		}
	};

	return (
		<Modal
			open={isOpen}
			aria-labelledby="modal-setup-fee-title"
			onClose={(_, reason) => {
				if (reason === "backdropClick") return;
				if (isLoading) return;
				onClose();
			}}
		>
			<Box sx={modalStyle}>
				<LoadingComp isopen={isLoading} />
				<Box position="relative" mb={0.5}>
					<Typography
						id="modal-setup-fee-title"
						variant="h5"
						color="primary"
						sx={{ fontWeight: 400, textAlign: "center", pr: 4 }}
					>
						Gerar taxa de setup
					</Typography>
					<IconButton
						aria-label="Fechar"
						onClick={onClose}
						size="small"
						disabled={isLoading}
						sx={{ position: "absolute", right: 0, top: "50%", transform: "translateY(-50%)" }}
					>
						<CloseIcon fontSize="small" />
					</IconButton>
				</Box>

				<Box mt={1.5}>
					<Alert
						severity="info"
						variant="standard"
						sx={{
							border: "none",
							borderRadius: 2,
							backgroundColor: (theme) => alpha(theme.palette.info.main, 0.08),
							"& .MuiAlert-icon": {
								color: "info.main",
								mt: "2px",
							},
							"& .MuiAlert-message": {
								py: 0,
							},
						}}
					>
						<Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.45 }}>
							Ao gerar a taxa de setup, será enviado um link para o e-mail do cliente.
							<br />
							Caso a análise seja contratada antes do pagamento, o link continuará disponível.
						</Typography>
					</Alert>

					<Box mt={1.25} display="flex" justifyContent="center">
						<Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems="center">
							<Chip
								icon={<Icon sx={{ fontSize: 18 }}>south</Icon>}
								label={`Mínimo: ${minLabel}`}
								variant="filled"
								sx={{
									fontWeight: 600,
									bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
									color: "primary.main",
									"& .MuiChip-icon": { color: "primary.main" },
								}}
							/>
							<Chip
								icon={<Icon sx={{ fontSize: 18 }}>north</Icon>}
								label={`Máximo: ${maxLabel}`}
								variant="filled"
								sx={{
									fontWeight: 600,
									bgcolor: (theme) => alpha(theme.palette.secondary.main, 0.08),
									color: "secondary.main",
									"& .MuiChip-icon": { color: "secondary.main" },
								}}
							/>
						</Stack>
					</Box>
				</Box>

				<Box mt={2}>
					<VForm
						key={isOpen ? `setup-fee` : "setup-fee-closed"}
						ref={formRef}
						onSubmit={handleSubmit}
						placeholder=""
						onPointerEnterCapture={() => {}}
						onPointerLeaveCapture={() => {}}
					>
						<Grid container spacing={2} justifyContent="center">
							<Grid item xs={12} md={6} display="flex" justifyContent="center">
								<VMaskTextField
									label="Valor da taxa"
									name="amount"
									mask="real"
									disabled={isLoading}
									helperText={`Informe um valor entre ${minLabel} e ${maxLabel}.`}
									sx={{ width: "min(320px, 100%)" }}
									onBlur={(e) => {
										const currentRaw = String((e.target as HTMLInputElement).value ?? "");
										const currentNumeric = Number(globalRemoveMaskReal2(currentRaw));
										if (!Number.isFinite(currentNumeric)) return;
										const clamped = clampToLimits(currentNumeric);
										formRef.current?.setFieldValue("amount", formatCurrencyInput(clamped));
									}}
									InputProps={{
										startAdornment: (
											<Box sx={{ mr: 1, color: "text.secondary" }}>
												<Typography variant="body2">R$</Typography>
											</Box>
										),
									}}
								/>
							</Grid>
						</Grid>

						<Box mt={3} display="flex" gap={2} justifyContent="center">
							<Button
								color="primary"
								disableElevation
								variant="outlined"
								startIcon={<Icon>arrow_back</Icon>}
								onClick={onClose}
								disabled={isLoading}
							>
								Voltar
							</Button>
							<Button
								color="primary"
								disableElevation
								variant="contained"
								startIcon={<Icon>check</Icon>}
								type="submit"
								disabled={isLoading}
							>
								Gerar
							</Button>
						</Box>
					</VForm>
				</Box>
			</Box>
		</Modal>
	);
};
